import {
  Stack,
  Tooltip,
  useBreakpointValue,
  useTheme,
  Text,
} from '@wegroup/design-system';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import LegacyAreYouSureModal from '../../../../../common/src/components/LegacyAreYouSureModal';
import {
  removeFromSelectedOffers,
  setPreferredOffer,
  addToQuoteSpecifications,
} from '../../../store/redux/slices/offersSlice';
import { IconActionClose } from 'wg-fe-ui';
import { RootState } from '../../../store/redux/reducers/rootReducer';
import { LouiseOffer } from '../../../../../../types/Quotes';
import RadioButton from '../../../../../flows/src/components/ui/advisory-flow/components/shared-components/RadioButton';
import { mq } from '../../../../../common/src/utils/styledComponentsUtils';
import LargeButton from '../../../../../common/src/components/LargeButton';
import TooltipCircle from '../../../../../common/src/components/TooltipCircle';
import { EXTEND } from '../../../../../../types/Campaigns';
import useTracking from '../../../../../common/src/hooks/tracking/useTracking';
import { getProductKey } from '../../../services/offersService';
import { isSocialLoginUrl } from '../../../../../app/src/features/authentication/constants/SocialAuth';

interface Props {
  offer: LouiseOffer;
}

const SingleSelectedOfferActions: React.FC<React.PropsWithChildren<Props>> = ({
  offer,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { colors } = useTheme();

  const isMobile = useBreakpointValue({
    base: false,
    mobileM: true,
    laptop: false,
  });

  const showTooltip = useBreakpointValue({
    base: false,
    laptop: true,
  });

  const { trackEvent } = useTracking();
  const [showModal, setShowModal] = useState(false);
  const preferredOffers = useSelector(
    (state: RootState) => state.offers.preferredOffers,
  );
  const campaign = useSelector(
    (state: RootState) => state.productFactory.campaign,
  );

  const handleOnRemoveOffer = () => {
    trackEvent('Remove offer from basket in Sales Conversation');
    // Resets the interval param as it can't be edited on another screen
    dispatch(
      addToQuoteSpecifications({
        productKey: getProductKey(offer),
        quoteId: offer.hash_id,
        value: { payment_interval: undefined },
      }),
    );

    // Remove is from selected offers
    dispatch(
      removeFromSelectedOffers({
        riskObject: offer.info.insurance_type,
        id: getProductKey(offer),
      }),
    );
  };

  const getPreferredOfferTranslationKey = () => {
    if (isSocialLoginUrl) {
      if (campaign.extend === EXTEND.INTERNAL_OFFER) {
        return 'ADVISORY-CONVERSATION_OFFERS_PREFERRED-OFFER_TOOLTIP-DESCRIPTION-SOCIAL-INTERNAL';
      }
      return 'ADVISORY-CONVERSATION_OFFERS_PREFERRED-OFFER_TOOLTIP-DESCRIPTION-SOCIAL-EXTERNAL';
    }
    return 'ADVISORY-CONVERSATION_OFFERS_PREFERRED-OFFER_TOOLTIP-DESCRIPTION-BROKER';
  };

  return (
    <>
      <LegacyAreYouSureModal
        showModal={showModal}
        handleNo={() => setShowModal(false)}
        handleYes={handleOnRemoveOffer}
        onClose={() => setShowModal(false)}
      >
        {t(
          'Are you sure that you want to remove this offer from your list of selected offers?',
        )}
      </LegacyAreYouSureModal>
      <GrayContainer>
        <Stack
          spacing="2"
          m={{ base: 4, laptop: 0 }}
          direction={{ base: 'column', laptop: 'row' }}
        >
          <RadioButton
            isSelected={
              preferredOffers[offer.info.insurance_type] === offer.hash_id
            }
            onClick={() =>
              dispatch(
                setPreferredOffer({
                  riskObject: offer.info.insurance_type,
                  id: offer.hash_id,
                }),
              )
            }
          >
            {t('Preferred offer')}
          </RadioButton>

          {showTooltip ? (
            <Tooltip label={t(getPreferredOfferTranslationKey())}>
              <TooltipCircle>?</TooltipCircle>
            </Tooltip>
          ) : (
            <Text color="text.gray" textStyle="smallBody">
              {t(
                'Marking an offer as preferred means that this offer will be send to the client by email once is it generated',
              )}
            </Text>
          )}
        </Stack>
        <Stack
          direction={{ base: 'row', laptop: 'column' }}
          spacing={{ base: 0, laptop: 2 }}
          pt={{ base: 0, laptop: 4 }}
        >
          <RemoveButton
            level="secondary"
            onClick={() => setShowModal(true)}
            {...(isMobile && {
              Icon: <StyledIconActionClose color={colors.danger.main} />,
              iconPosition: 'left',
            })}
          >
            {t('Remove offer')}
          </RemoveButton>
        </Stack>
      </GrayContainer>
    </>
  );
};

const StyledIconActionClose = styled(IconActionClose)`
  padding: 0.4rem;
`;

const GrayContainer = styled.div`
  border-top: 1px solid ${({ theme }) => theme.gray[50]};
  &:last-child {
    border-bottom-right-radius: 6px;
    border-bottom-left-radius: 6px;
  }

  > h3 {
    margin-bottom: 1.2rem;
  }

  &:first-child {
    display: none;
    ${mq.laptop} {
      display: block;
    }
  }

  ${mq.laptop} {
    border: none;
    background-color: #f7f8fb;
    border-radius: 6px;
    padding: 1.6rem;
  }
`;

const StyledLargeButton = styled(LargeButton)`
  min-width: unset;
  padding: 0.8rem;
  min-height: 4.3rem;
  width: 100%;
  border-radius: 0;

  &:first-child {
    border-bottom-left-radius: 6px;
  }

  &:last-child {
    border-bottom-right-radius: 6px;
  }

  ${mq.laptop} {
    border-radius: 5px;
  }
`;

const RemoveButton = styled(StyledLargeButton)`
  background-color: ${({ theme }) => theme.status.errorLight};
  color: ${({ theme }) => theme.status.error};

  &:hover {
    background-color: ${({ theme }) => theme.status.errorLight};
  }
`;

export default SingleSelectedOfferActions;
